import { DOCUMENT } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SpinnerModule } from '@jfw/angular/ecommerce/common-components/spinner';
import { PLATFORM_PORTAL_ENVIRONMENT } from '@jfw/angular/platform/tokens';
import {
  AccountService,
  isAccountContextSuite,
} from '@jfw/angular/shared/services';
import { AccountSearchResult } from '@jfw/types';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';

const ACCOUNT_SEARCH_MIN_LENGTH = 2;

interface AccountSetterForm {
  dealerId: FormControl<string | null>;
  callerName: FormControl<string>;
}
@Component({
  selector: 'app-account-setter',
  templateUrl: './account-setter.component.html',
  styleUrls: ['./account-setter.component.scss'],
  // EXT-6537: this is the Account # · Caller · GO lookup; the account bar
  // itself is app-account-banner, which EXT-6573 projects this into.
  host: {
    '[class.account-lookup]': 'displayAccountSetter()',
  },
  imports: [
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    SpinnerModule,
  ],
})
export class AccountSetterComponent {
  private readonly environment = inject(PLATFORM_PORTAL_ENVIRONMENT);
  private readonly accountService = inject(AccountService);
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  protected readonly platformAccount = this.accountService._platformAccount;
  protected readonly currentSuite = toSignal(this.authService.currentSuite$);
  protected readonly loading = this.accountService.loadingSignal;
  protected readonly errorMessage = this.accountService.errorSignal;
  private readonly destroy$ = new Subject<void>();

  protected readonly displayAccountSetter = computed(() =>
    isAccountContextSuite(this.currentSuite()?.enum),
  );

  // EXT-6014: typeahead suggestions for the Account field, populated by a
  // debounced OMNI_ACCOUNTMASTERVIEW_V2 lookup as the user types.
  protected readonly suggestions = signal<AccountSearchResult[]>([]);
  // EXT-6013: the account number activateAccount() is currently in the
  // middle of activating, if any -- see the debounced pipeline's own guard
  // in ngOnInit for why this needs to be tracked separately from
  // platformAccount().
  private activatingAccountNumber: string | undefined;

  protected readonly form = new FormGroup<AccountSetterForm>({
    dealerId: new FormControl(
      this.accountService.platformAccount?.accountNumber ?? '',
      {
        nonNullable: true,
        validators: [
          Validators.required,
          // EXT-6014: the exact-5-character requirement moved to onSubmit()
          // (a 'length' error, set only at GO-click time) rather than living
          // here as minLength/maxLength -- those would flag a longer,
          // in-progress typeahead search term (e.g. "stroudsburg") as
          // invalid the moment the field is touched, well before the user
          // has any intent to submit it as a literal account number.
          // This validator ONLY returns an error if the service signal is true
          () =>
            this.accountService.errorMessage() ? { serverError: true } : null,
        ],
      },
    ),
    callerName: new FormControl(this.accountService.callerName() ?? '', {
      nonNullable: true,
      validators: [Validators.maxLength(10)],
    }),
  });

  // EXT-5972: a matTooltip (rendered via CDK Overlay, outside this bar's own
  // DOM/stacking context entirely) rather than an inline mat-error -- the
  // account bar's tight 56px row has no room for a second text line
  // without either clipping it or having it visually overlap module-header
  // underneath, confirmed live no matter how the overflow/z-index/opaque-
  // background were tuned.
  // Invalid once touched, or straight away for a server-side (not found)
  // error, as the old ErrorStateMatcher did. EXT-6573: also straight away when
  // no account is loaded, so the empty landing state shows the required look.
  protected dealerIdInvalid(): boolean {
    const control = this.form.controls.dealerId;
    return (
      control.invalid &&
      (control.touched ||
        control.hasError('serverError') ||
        !this.platformAccount())
    );
  }

  protected dealerIdErrorMessage(): string {
    const control = this.form.controls.dealerId;
    if (control.hasError('length')) {
      return 'Account must be 5 characters.';
    }
    if (control.hasError('serverError')) {
      return 'Invalid account number.';
    }
    return 'Account is required.';
  }

  constructor() {
    effect(() => {
      const account = this.platformAccount();
      const caller = this.accountService.callerName();

      untracked(() => {
        if (account?.accountNumber) {
          // Update the form value if it differs from the signal
          if (this.form.controls.dealerId.value !== account.accountNumber) {
            this.form.controls.dealerId.setValue(account.accountNumber);
          }
        }

        if (caller && this.form.controls.callerName.value !== caller) {
          this.form.controls.callerName.setValue(caller);
        }
      });
    });

    effect(() => {
      // 1. Listen to the signal
      const hasError = this.errorMessage();

      // 2. Use untracked to modify the form safely
      untracked(() => {
        const control = this.form.controls.dealerId;
        if (hasError) {
          control.enable();
          // Force execution to the next tick to ensure the 'enable' is processed
          setTimeout(() => {
            control.setErrors({ serverError: true });
            control.markAsTouched();
            control.updateValueAndValidity();
          });
        }
      });
    });
  }

  ngOnInit() {
    this.accountService.setApiUrl(this.environment.account_rest_api_server_url);
    this.accountService.setAccountContextApiUrl(
      this.environment.account_context_api_url,
    );
    this.accountService.setAccountSearchApiUrl(
      this.environment.account_search_api_url,
    );
    this.accountService.setDeliveryApiUrl(
      this.environment.delivery_api_server_url,
    );
    this.accountService.setCommentsApiUrl(this.environment.comments_api_url);
    this.accountService.setPromotionsApiUrl(
      this.environment.promotions_api_url,
    );
    this.accountService.setDealerUserApiUrl(
      this.environment.dealerUser_rest_api_server_url,
    );

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => this.activateAccountFromQueryParam(params));

    // EXT-6207: an account survives a page reload through sessionStorage, but
    // *only the account itself* -- AccountService restores `_platformAccount`
    // in a field initializer and fetches nothing, so the delivery days, vans,
    // parcel carrier, preferred carrier and comments that setPlatformAccount()
    // normally loads alongside it (one combined call, EXT-6012) all sit at
    // their empty initial values. The load bar reads exactly those signals, so
    // after a reload every carrier orb showed the "unknown" gray, the next-van
    // date was missing from the Shipping label and the comment count read 0 --
    // until the user opened the drawer and its tab component fetched them on
    // its own mount, which looked like the bar being broken rather than unloaded.
    //
    // Runs the same activation path an explicit GO would, but only when nothing
    // else has already begun one: the queryParams subscription above fires
    // synchronously on subscribe, so a `?actNum=` landing has already set
    // `activatingAccountNumber` by the time this line runs and must not be
    // fetched a second time.
    // Deliberately NOT routed through activateAccount(): that path is built for
    // an activation the user asked for, so it disables the field for the
    // duration and, on failure, stamps "Invalid account number." on it via the
    // errorMessage signal -- neither of which belongs on a background refresh
    // of an account the user never re-entered, and the error does not clear
    // until the next real activation. `silent` keeps every signal populated
    // while leaving errorMessage alone; a failed rehydrate just leaves the bar
    // as unpopulated as it was before this ran.
    //
    // EXT-6573: an Order Entry deep link (?accountNumber=) loads its own
    // account in Order Entry's ngOnInit. Restoring the old account here as
    // well races it, and when the restore lands second the account flips back
    // and Order Entry's account-mismatch guard never loads the order.
    const restoredAccountNumber = this.platformAccount()?.accountNumber;
    if (
      restoredAccountNumber &&
      !this.activatingAccountNumber &&
      !this.isOrderEntryDeepLink()
    ) {
      this.accountService
        .setPlatformAccount(restoredAccountNumber, { silent: true })
        .catch(() => undefined);
    }

    // EXT-5910: no debounce here -- accountService.callerName is read
    // synchronously by order-entry's convertToOrderType() at submit time
    // (both for the eager shipping-disposition prefetch and the real
    // submission). A debounce left the signal briefly stale after typing,
    // which could send a blank/partial callerFirstName to the backend
    // (rejected by GetShippingDispositionRequestSchema's >=2-char minimum)
    // if a CSR typed the Caller Name and submitted within the delay window.
    this.form
      .get('callerName')!
      .valueChanges.pipe(
        distinctUntilChanged(), // ignore same value
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        this.onCallerNameChanged(value);
      });

    // EXT-5582: clearing the Account field has no effect on the active
    // account today (it only ever changes via onSubmit()'s GO/Enter path) —
    // return to the empty state and close the drawer the moment it's
    // actually emptied out, matching the ticket's own AC.
    this.form.controls.dealerId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!value?.trim() && this.platformAccount()) {
          this.accountService.clearPlatformAccount();
          this.accountService.showDrawer.set(false);
        }
      });

    // EXT-6014: debounced typeahead lookup, independent of the field's own
    // exact-5-character validators -- those only gate the manual GO path.
    this.form.controls.dealerId.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => {
          const term = (value ?? '').trim();
          if (term.length < ACCOUNT_SEARCH_MIN_LENGTH) {
            return of([]);
          }
          // EXT-6013: skip searching for whatever's currently active OR
          // currently *being* activated. The "currently active" half alone
          // isn't enough -- if the debounced 300ms window from the user's
          // own typing elapses while activateAccount()'s async
          // setPlatformAccount() call is still in flight (e.g. typing an
          // account number quickly and hitting Enter right after), this
          // pipeline's own pending emission for that typed value can fire
          // *before* platformAccount() has actually updated, so that check
          // alone wouldn't catch it. activatingAccountNumber is set
          // synchronously the instant activateAccount() is called (covering
          // that whole race window) and cleared once it settles, at which
          // point platformAccount() has already updated and the other half
          // of this check takes over -- together they cover the entire
          // activation lifecycle, not just its aftermath (also covers
          // activateAccount()'s own control.enable()/updateValueAndValidity()
          // re-emitting valueChanges with the just-activated value once
          // activation completes).
          const upperTerm = term.toUpperCase();
          if (
            upperTerm === this.platformAccount()?.accountNumber ||
            upperTerm === this.activatingAccountNumber
          ) {
            return of([]);
          }
          return this.accountService
            .searchAccounts(term)
            .pipe(catchError(() => of([])));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((results) => this.suggestions.set(results));

    // this.form.controls.dealerId.valueChanges.pipe(
    //   takeUntil(this.destroy$)
    // ).subscribe(() => {
    //   if (this.form.controls.dealerId.hasError('serverError')) {
    //     this.form.controls.dealerId.setErrors(null);
    //     this.form.controls.dealerId.updateValueAndValidity();
    //   }
    // });
  }

  // Read from the browser's location, not the router: this runs once at app
  // start, before the router has finished its first navigation.
  private isOrderEntryDeepLink(): boolean {
    const { pathname, search } = this.document.location;
    return (
      pathname.startsWith('/wholesale/order-entry') &&
      !!new URLSearchParams(search).get('accountNumber')?.trim()
    );
  }

  private onCallerNameChanged(value: string): void {
    this.accountService.callerName.set(value);
  }

  // Auto-selects the account when a route lands here with ?actNum= set
  // (e.g. a Dialpad incoming-call screenpop), reusing the same activation
  // path as a manual dealerId submit. Subscribed to queryParams (not a
  // one-time snapshot) since this component is a permanent app-shell
  // sibling of <router-outlet> that never re-runs ngOnInit -- a later
  // screenpop, or a post-sign-in redirect back to the same URL, must still
  // be picked up. Strips the param afterward so it doesn't re-fire on
  // subsequent in-app navigation.
  private activateAccountFromQueryParam(params: Params): void {
    const actNum = params['actNum']?.trim();
    if (!actNum || actNum.length !== 5) {
      return;
    }
    // EXT-5800: only the Dialpad screenpop (/wholesale/data-pods?actNum=,
    // redirected to /wholesale/dashboard since EXT-6537)
    // should activate an account this way. Geolocation keeps its own page
    // state in ?actNum=, and honouring it there made every lookup the
    // portal-wide account -- and stripped the param Geolocation depends on.
    if (!this.router.url.startsWith('/wholesale/')) {
      return;
    }

    const normalized = actNum.toUpperCase();
    if (normalized !== this.platformAccount()?.accountNumber) {
      this.activateAccount(normalized);
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { actNum: null },
      queryParamsHandling: 'merge',
    });
  }

  // Resolves true when the account actually loaded.
  private async activateAccount(val: string): Promise<boolean> {
    let loaded = false;
    const control = this.form.controls.dealerId;
    // EXT-6013: clear immediately (rather than relying solely on the
    // debounced pipeline's own active-account guard below) so the
    // autocomplete panel closes right away for every activation path that
    // funnels through here -- GO, a typeahead selection, and query-param
    // activation.
    this.suggestions.set([]);
    // EXT-6013: set synchronously, before the async setPlatformAccount()
    // call below, so the debounced typeahead pipeline's own guard (see
    // ngOnInit) can tell "this account is being activated" apart from
    // "nothing is happening" for the whole duration of that call -- not
    // just after it resolves. See that guard's own comment for the race it
    // closes (typing an account number quickly and hitting Enter right
    // after).
    this.activatingAccountNumber = val;
    // EXT-5497v3: set the value before disabling -- disable()/enable()/
    // updateValueAndValidity() below all re-emit valueChanges with the
    // control's *current* value, and the EXT-5582 "field cleared" listener
    // (below) reads any empty emission as the user having cleared the field.
    // If the control's displayed value hasn't yet been synced to the
    // just-activated account (a real race against the platformAccount()
    // effect above) by the time enable() re-emits, that listener would
    // otherwise misfire and wipe out the account this call just loaded.
    control.setValue(val, { emitEvent: false });
    control.disable();

    try {
      await this.accountService.setPlatformAccount(val);
      // EXT-6505: an account the user (or a Dialpad screenpop) asked for
      // opens the drawer on Shipping. Set directly rather than through
      // selectDrawerTab(), which would *close* a drawer already on Shipping
      // when a second account is loaded. The silent session rehydrate in
      // ngOnInit doesn't come through here, so a page reload stays closed.
      if (this.platformAccount()?.accountNumber === val) {
        loaded = true;
        this.accountService.drawerState.set('shipping');
        this.accountService.showDrawer.set(true);
      }
    } catch (e) {
      // Error is handled by the service setting the signal
    } finally {
      control.enable();
      // 2. IMPORTANT: Manually trigger one final validation check
      // This will run our custom validator, see the Signal is true, and set the error
      control.updateValueAndValidity();
      control.markAsTouched();
      // EXT-6013: platformAccount() has updated by now (setPlatformAccount()
      // above has resolved), so the debounced pipeline's other guard takes
      // over from here -- safe to clear.
      this.activatingAccountNumber = undefined;
    }
    return loaded;
  }

  // EXT-6557: an account the user picks (GO or a typeahead selection) opens
  // on the Dashboard. The Dialpad screenpop already lands there, and the
  // silent rehydrate on page reload doesn't come through here.
  private async activateAccountAndOpenDashboard(val: string): Promise<void> {
    if (await this.activateAccount(val)) {
      await this.router.navigateByUrl('/wholesale/dashboard');
    }
  }

  protected async onSubmit() {
    const control = this.form.controls.dealerId;

    // 1. Reset service error before starting a new request
    this.accountService.errorMessage.set(false);
    // Re-validate to clear the 'serverError' red text immediately
    control.updateValueAndValidity();

    const val = (control.value ?? '').trim().toUpperCase();

    // EXT-6014: the field accepts any length while searching for
    // suggestions -- the exact-5-character requirement is enforced only
    // here, at the point the user asks to submit it as a literal account
    // number via GO, not as a live validator (see the form's own comment).
    if (val.length !== 5) {
      control.setErrors({ ...(control.errors ?? {}), length: true });
    }

    if (control.invalid) {
      control.markAsTouched();
      return;
    }

    await this.activateAccountAndOpenDashboard(val);
  }

  // EXT-6014: selecting a typeahead result activates that account the same
  // way typing the full 5-character number and clicking GO does today.
  protected async onSuggestionSelected(accountNumber: string): Promise<void> {
    await this.activateAccountAndOpenDashboard(accountNumber);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*
  private applyServerError() {
    const control = this.form.controls.dealerId;

    // 2. Enable first - Mandatory for the status to change from VALID to INVALID
    control.enable();

    // 3. Move to the next macro-task to ensure the 'enabled' state is processed
    setTimeout(() => {
      control.setErrors({ serverError: true });
      // This forces the internal 'invalid' property to true
      control.markAsTouched();
      control.updateValueAndValidity({ emitEvent: true });

      console.log('New Control Status:', control.status); // Should now be INVALID
      console.log('New Control Errors:', control.errors); // Should be { serverError: true }
    }, 10);
  }

  get db2Attributes() {
    const account = this.platformAccount();
    if (!account) return {}; // return empty object if account is undefined
    const {
      ADR1 = '',
      ADR2 = '',
      ANAME = '',
      CITY = '',
      ZIPZN = '',
      STADTE = '',
    } = account.db2AccountAttributes || {};

    function toCapitalCase(str: string): string {
      if (!str) return '';
      return str
        .split(' ')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ');
    }
    return {
      ADR1: toCapitalCase(ADR1),
      ADR2: toCapitalCase(ADR2),
      ANAME: toCapitalCase(ANAME),
      CITY: toCapitalCase(CITY),
      ZIPZN: toCapitalCase(ZIPZN),
    };
  }
  */
}
